import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { PageSkeleton } from '@v2/design-system';
import TvLayout from './layouts/TvLayout';
import FinanceTvPage from './pages/FinanceTvPage';
import NotFoundPage from './pages/NotFoundPage';
import ForbiddenPage from './pages/ForbiddenPage';
import ServerErrorPage from './pages/ServerErrorPage';
import SystemIntegrationsPage from './pages/SystemIntegrationsPage';
import EmailNotificationsPage from './pages/EmailNotificationsPage';
import TaskSlaSettingsPage from './pages/TaskSlaSettingsPage';
import HydrogenLayout from './layouts/HydrogenLayout';
import ComexDashboardPage from './pages/ComexDashboardPage';
import ExecutiveDashboardPage from './pages/ExecutiveDashboardPage';
import FinanceDashboardPage from './pages/FinanceDashboardPage';
import EstoqueDashboardPage from './pages/EstoqueDashboardPage';
import StockApprovalsPage from './pages/StockApprovalsPage';
import StockConsultPage from './pages/StockConsultPage';
import StockReservationDetailPage from './pages/StockReservationDetailPage';
import StockReservationSettingsPage from './pages/StockReservationSettingsPage';
import ContainerCalendarPage from './pages/ContainerCalendarPage';
import ProductDetailPage from './pages/ProductDetailPage';
import ProductFormPage from './pages/ProductFormPage';
import ProductListPage from './pages/ProductListPage';
import ClientListPage from './pages/ClientListPage';
import ClientFormPage from './pages/ClientFormPage';
import OrderFormPage from './pages/OrderFormPage';
import OrderListPage from './pages/OrderListPage';
import UserListPage from './pages/UserListPage';
import RolesPermissionsPage from './pages/RolesPermissionsPage';
import LoginSecuritySettingsPage from './pages/LoginSecuritySettingsPage';
import IntranetConfigPage from './pages/IntranetConfigPage';
import { AnnouncementsPage, EventsPage } from './pages/company/CompanyPages';
import { DocumentDetailPage, DocumentEditPage, DocumentsPage } from './pages/company/DocumentPages';
import DocumentPrintPage from './pages/company/DocumentPrintPage';
import UserDetailPage from './pages/UserDetailPage';
import UserFormPage from './pages/UserFormPage';
import UserActivityLogPage from './pages/UserActivityLogPage';
import ProfilePage from './pages/ProfilePage';
import PloomesAttachmentListPage from './pages/PloomesAttachmentListPage';
import PloomesDealDetailPage from './pages/PloomesDealDetailPage';
import PloomesDealListPage from './pages/PloomesDealListPage';
import HomePage from './pages/HomePage';
import ShipmentDetailPage from './pages/ShipmentDetailPage';
import ShipmentFormPage from './pages/ShipmentFormPage';
import ShipmentListPage from './pages/ShipmentListPage';
import ShipmentOperationalPage from './pages/ShipmentOperationalPage';
import ShipmentPurchaseOrderPage from './pages/ShipmentPurchaseOrderPage';
import ShipmentProformaInvoicePage from './pages/ShipmentProformaInvoicePage';
import ConferencePackageScanPage from './pages/ConferencePackageScanPage';
import ShipmentConferencePage from './pages/ShipmentConferencePage';
import EntityDetailPage from './pages/EntityDetailPage';
import ClientDetailPage from './pages/ClientDetailPage';
import FranqueadoraListPage from './pages/FranqueadoraListPage';
import FranqueadoraFormPage from './pages/FranqueadoraFormPage';
import ProductColorsPage from './pages/ProductColorsPage';
import DistributionCentersPage from './pages/DistributionCentersPage';
import DistributionCenterFormPage from './pages/DistributionCenterFormPage';
import CustomsBrokersPage from './pages/CustomsBrokersPage';
import CustomsBrokerDetailPage from './pages/CustomsBrokerDetailPage';
import StageColorsPage from './pages/StageColorsPage';
import CommissionLayout from './pages/commissioning/CommissionLayout';
import CommissionDashboard from './pages/commissioning/CommissionDashboard';
import CommissionGoalsPage from './pages/commissioning/CommissionGoalsPage';
import CommissionGoalBucketAnalysisPage from './pages/commissioning/CommissionGoalBucketAnalysisPage';
import CommissionStructuresPage from './pages/commissioning/CommissionStructuresPage';
import CommissionModifiersPage from './pages/commissioning/CommissionModifiersPage';
import CommissionSettingsPage from './pages/commissioning/CommissionSettingsPage';
import CommissionApproversPage from './pages/commissioning/CommissionApproversPage';
import GluteleaderBeneficiaryPage from './pages/commissioning/GluteleaderBeneficiaryPage';
import SalesTeamsPage from './pages/commissioning/SalesTeamsPage';
import SellerAssignmentPeriodsPage from './pages/commissioning/SellerAssignmentPeriodsPage';
import SubmitCommissionNfPage from './pages/commissioning/SubmitCommissionNfPage';
import CommissionNfEmailPreviewPage from './pages/commissioning/CommissionNfEmailPreviewPage';
import CommissionRecordsPage from './pages/commissioning/CommissionRecordsPage';
import CommissionMonthlyNfsPage from './pages/commissioning/CommissionMonthlyNfsPage';
import CommissionMonthlyNfDetailPage from './pages/commissioning/CommissionMonthlyNfDetailPage';
import SellerHistoryPage from './pages/commissioning/SellerHistoryPage';
import SellerCommissionTimelinePage from './pages/commissioning/SellerCommissionTimelinePage';
import CommissionAuditPage from './pages/commissioning/CommissionAuditPage';
import CommissionExceptionAuditPage from './pages/commissioning/CommissionExceptionAuditPage';
import CommissionCashflowPage from './pages/commissioning/CommissionCashflowPage';
import CommissionPaymentApprovalPage from './pages/commissioning/CommissionPaymentApprovalPage';
import CommissionPaymentApprovalDetailPage from './pages/commissioning/CommissionPaymentApprovalDetailPage';
import CommissionPaymentRulesPage from './pages/commissioning/CommissionPaymentRulesPage';
import PloomesFieldInspectorPage from './pages/commissioning/PloomesFieldInspectorPage';
import CommissionOrgChartPage from './pages/commissioning/CommissionOrgChartPage';
import CommissionSalesCalendarPage from './pages/commissioning/CommissionSalesCalendarPage';
import CommissionDealDetailPage from './pages/commissioning/CommissionDealDetailPage';
import GluteleaderDashboardPage from './pages/commissioning/GluteleaderDashboardPage';
import BucklerOnePage from './pages/commissioning/BucklerOnePage';
import { ResourceFormRoute, ResourceListRoute } from './features/crud/ResourceCrudPage';
import RequireRole from './components/RequireRole';
import RequireCan from './components/RequireCan';
import DefaultedInstallmentsPage from './pages/DefaultedInstallmentsPage';
import SalesExceptionsPage from './pages/SalesExceptionsPage';
import RequireCanInspectPloomesFields from './components/RequireCanInspectPloomesFields';
import {
    ADMIN_ROLES,
    ALL_OPERATIONAL_ROLES,
    COMEX_ROLES,
    COMMISSION_ROLES,
    CRM_ROLES,
    CX_ROLES,
    FINANCIAL_ROLES,
    LOGISTICS_ROLES,
    SALES_ROLES,
    canAccessCommissionModule,
    canViewDefaulted,
    canViewGluteleader,
    canViewProducts,
    canViewStockConsult,
    isSuperAdmin,
} from './lib/auth';

const EquipmentPage = lazy(() => import('./pages/EquipmentConfiguratorPage'));

function Loading() {
    return (
        <div className="p-4">
            <PageSkeleton variant="page" />
        </div>
    );
}

/** Redirect that interpolates route params into the target path. */
function LegacyParamRedirect({ to }: { to: (params: Record<string, string | undefined>) => string }) {
    const params = useParams();
    return <Navigate to={to(params)} replace />;
}

export function AppRouter() {
    const boot = window.__V2__;

    if (!boot?.user) {
        window.location.href = boot?.loginUrl ?? '/login';
        return null;
    }

    return (
        <Routes>
            {/* ─── TV layout — apenas financeiro/admin ─────────────────── */}
            <Route path="company/documents/:id/print" element={<DocumentPrintPage />} />
            <Route element={<TvLayout />}>
                <Route element={<RequireRole roles={FINANCIAL_ROLES} />}>
                    <Route path="finance/tv" element={<FinanceTvPage />} />
                </Route>
            </Route>

            <Route element={<HydrogenLayout />}>

                {/* ─── Página de acesso negado ─────────────────────────── */}
                <Route path="forbidden" element={<ForbiddenPage />} />

                {/* ─── Páginas abertas para qualquer usuário autenticado ─ */}
                <Route index element={<HomePage />} />
                <Route
                    element={
                        <RequireRole
                            roles={[
                                'super_admin',
                                'admin',
                                'comex',
                                'financeiro',
                                'financial',
                                'financial_director',
                                'accounting',
                                'sales_manager',
                                'commercial_director',
                                'hr',
                            ]}
                        />
                    }
                >
                    <Route path="dashboard/executive" element={<ExecutiveDashboardPage />} />
                </Route>
                <Route
                    element={
                        <RequireRole
                            roles={[...FINANCIAL_ROLES, 'financial_director', 'accounting']}
                        />
                    }
                >
                    <Route path="dashboard/finance" element={<FinanceDashboardPage />} />
                </Route>
                <Route path="profile" element={<ProfilePage />} />
                <Route path="calendar/containers" element={<ContainerCalendarPage />} />

                <Route element={<RequireCan check={canViewProducts} />}>
                    <Route path="products" element={<ProductListPage />} />
                    <Route path="products/:id" element={<ProductDetailPage />} />
                </Route>

                <Route element={<RequireCan check={canViewStockConsult} />}>
                    <Route path="estoque/consulta" element={<StockConsultPage />} />
                    <Route path="estoque/consulta/:sku" element={<StockConsultPage />} />
                </Route>

                {/* Influenciador GL com acesso ao Hub (pode não ter perfil de vendedor); API checa o vínculo. */}
                <Route path="my-gluteleader" element={<GluteleaderBeneficiaryPage self />} />

                {/* ─── My Commission — seller self-service ─────────────── */}
                <Route element={<RequireCan check={canAccessCommissionModule} />}>
                    <Route path="my-commission" element={<SellerHistoryPage />} />
                    <Route path="my-commission/history" element={<SellerCommissionTimelinePage />} />
                    <Route path="my-commission/history/:month" element={<SellerCommissionTimelinePage />} />
                    <Route path="my-commission/submit-nf/:recordId" element={<SubmitCommissionNfPage />} />
                    <Route path="my-commission/monthly-nf/:nfId" element={<CommissionMonthlyNfDetailPage />} />
                    {/* Legacy PT redirects → English paths */}
                    <Route path="minha-comissao" element={<Navigate to="/my-commission" replace />} />
                    <Route
                        path="minha-comissao/enviar-nf/:recordId"
                        element={<LegacyParamRedirect to={(p) => `/my-commission/submit-nf/${p.recordId}`} />}
                    />
                    <Route
                        path="minha-comissao/nf-mensal/:nfId"
                        element={<LegacyParamRedirect to={(p) => `/my-commission/monthly-nf/${p.nfId}`} />}
                    />
                </Route>

                {/* ─── Empresa / Intranet ──────────────────────────────── */}
                <Route path="company/announcements" element={<AnnouncementsPage />} />
                <Route path="company/events" element={<EventsPage />} />
                <Route path="company/documents" element={<DocumentsPage />} />
                <Route path="company/documents/new" element={<DocumentEditPage />} />
                <Route path="company/documents/:id" element={<DocumentDetailPage />} />
                <Route path="company/documents/:id/edit" element={<DocumentEditPage />} />

                {/* ─── Embarques — todos os roles operacionais ─────────── */}
                <Route element={<RequireRole roles={ALL_OPERATIONAL_ROLES} />}>
                    <Route path="shipments" element={<ShipmentListPage />} />
                    <Route path="shipments/:id" element={<ShipmentDetailPage />} />
                    <Route path="shipments/:id/documents" element={<ShipmentOperationalPage title="Documentos" pathKey="documents" />} />
                    <Route path="shipments/:id/purchase-order" element={<ShipmentPurchaseOrderPage />} />
                    <Route path="shipments/:id/pi-check" element={<ShipmentProformaInvoicePage />} />
                </Route>

                {/* ─── Criar/editar embarques — COMEX + admin ──────────── */}
                <Route element={<RequireRole roles={COMEX_ROLES} />}>
                    <Route path="shipments/new" element={<ShipmentFormPage />} />
                    <Route path="shipments/:id/edit" element={<ShipmentFormPage />} />
                    <Route path="products/:id/edit" element={<ProductFormPage />} />
                </Route>

                {/* ─── Reservas de estoque — logística + comercial ─────── */}
                <Route
                    element={
                        <RequireRole
                            roles={[
                                ...LOGISTICS_ROLES,
                                'salesperson',
                                'sales_manager',
                                'commercial_director',
                            ]}
                        />
                    }
                >
                    <Route path="estoque/aprovacoes" element={<StockApprovalsPage />} />
                    <Route path="estoque/reservas/:id" element={<StockReservationDetailPage />} />
                </Route>

                {/* ─── Conferência/recebimento — logística + COMEX ─────── */}
                <Route element={<RequireRole roles={LOGISTICS_ROLES} />}>
                    <Route path="estoque" element={<EstoqueDashboardPage />} />
                    <Route path="shipments/:id/conference" element={<ShipmentConferencePage />} />
                    <Route
                        path="shipments/:id/conference/package/:checkItemId/:packageIndex"
                        element={<ConferencePackageScanPage />}
                    />
                    <Route path="p/:shipmentId/:checkItemId/:packageIndex" element={<ConferencePackageScanPage />} />
                    <Route path="shipments/:id/check" element={<ShipmentOperationalPage title="Conferência" pathKey="check" />} />
                    <Route path="shipments/:id/receive-cd" element={<ShipmentOperationalPage title="Recebimento CD" pathKey="receive_cd" />} />
                </Route>

                {/* ─── CRM operacional — COMEX/Financeiro ─────────────── */}
                <Route element={<RequireRole roles={CRM_ROLES} />}>
                    <Route path="comex" element={<ComexDashboardPage />} />
                    <Route
                        path="equipment-configurator"
                        element={
                            <Suspense fallback={<Loading />}>
                                <EquipmentPage />
                            </Suspense>
                        }
                    />
                    <Route path="ploomes-attachments" element={<PloomesAttachmentListPage />} />
                    <Route path="ploomes-attachments/:id" element={<EntityDetailPage entityKey="ploomes-attachments" />} />
                </Route>

                {/* ─── Vendas / Propostas Ganhas — comercial + CRM ─────── */}
                <Route element={<RequireRole roles={SALES_ROLES} />}>
                    <Route path="ploomes-deals" element={<PloomesDealListPage />} />
                    <Route path="ploomes-deals/:id" element={<PloomesDealDetailPage />} />
                </Route>

                {/* ─── Clientes + Pedidos — CX/COMEX/Financeiro ────────── */}
                <Route element={<RequireRole roles={CX_ROLES} />}>
                    <Route path="clients" element={<ClientListPage />} />
                    <Route path="clients/new" element={<ClientFormPage />} />
                    <Route path="clients/:id" element={<ClientDetailPage />} />
                    <Route path="clients/:id/edit" element={<ClientFormPage />} />
                </Route>

                {/* ─── Administração — admin + super_admin ─────────────── */}
                <Route element={<RequireRole roles={ADMIN_ROLES} />}>
                    <Route path="estoque/config-reservas" element={<StockReservationSettingsPage />} />
                    <Route path="users" element={<UserListPage />} />
                    <Route path="users/new" element={<UserFormPage />} />
                    <Route path="users/:id" element={<UserDetailPage />} />
                    <Route path="users/:id/edit" element={<UserDetailPage />} />
                    <Route path="users/:id/activity-log" element={<UserActivityLogPage />} />
                    {isSuperAdmin() && <Route path="sales-exceptions" element={<SalesExceptionsPage />} />}
                    <Route path="franchisors" element={<FranqueadoraListPage />} />
                    <Route path="franchisors/new" element={<FranqueadoraFormPage />} />
                    <Route path="franchisors/:id/edit" element={<FranqueadoraFormPage />} />
                    <Route path="settings/stage-colors" element={<StageColorsPage />} />
                    <Route path="settings/product-colors" element={<ProductColorsPage />} />
                    <Route path="settings/distribution-centers" element={<DistributionCentersPage />} />
                    <Route path="settings/distribution-centers/new" element={<DistributionCenterFormPage />} />
                    <Route path="settings/distribution-centers/:id/edit" element={<DistributionCenterFormPage />} />
                    <Route path="settings/customs-brokers" element={<CustomsBrokersPage />} />
                    <Route path="settings/customs-brokers/new" element={<CustomsBrokersPage />} />
                    <Route path="settings/customs-brokers/:id" element={<CustomsBrokerDetailPage />} />
                    <Route path="settings/customs-brokers/:id/edit" element={<CustomsBrokersPage />} />
                    <Route path="settings/task-sla" element={<TaskSlaSettingsPage />} />
                    <Route path="settings/:resourceKey" element={<ResourceListRoute />} />
                    <Route path="settings/:resourceKey/:id/edit" element={<ResourceFormRoute />} />
                    <Route path="settings/:resourceKey/new" element={<ResourceFormRoute />} />
                </Route>

                {/* ─── Configurações sensíveis — apenas super_admin ─────── */}
                <Route element={<RequireRole roles={['super_admin']} />}>
                    <Route path="roles-permissions" element={<RolesPermissionsPage />} />
                    <Route path="settings/login-security" element={<LoginSecuritySettingsPage />} />
                    <Route path="settings/system-integrations" element={<SystemIntegrationsPage />} />
                    <Route path="settings/email-notifications" element={<EmailNotificationsPage />} />
                    <Route path="settings/intranet" element={<IntranetConfigPage />} />
                </Route>

                {/* ─── Inadimplências — fora do grupo admin: CFO e Contas a Receber também veem ─── */}
                {canViewDefaulted() && <Route path="defaulted-installments" element={<DefaultedInstallmentsPage />} />}

                {/* ─── GLUTELEADER — fora da trava do módulo: CFO só visualiza (View:GluteleaderDashboard) ─── */}
                {canViewGluteleader() && (
                    <Route path="commissioning" element={<CommissionLayout />}>
                        <Route path="gluteleader" element={<GluteleaderDashboardPage />} />
                        <Route path="gluteleader/beneficiaries/:beneficiaryId" element={<GluteleaderBeneficiaryPage />} />
                    </Route>
                )}

                {/* ─── Comissionamento (gestão) — permission + role ─────── */}
                <Route element={<RequireCan check={canAccessCommissionModule} />}>
                <Route element={<RequireRole roles={COMMISSION_ROLES} />}>
                    <Route path="commissioning" element={<CommissionLayout />}>
                        <Route index element={<CommissionDashboard />} />
                        <Route path="sales-calendar" element={<CommissionSalesCalendarPage />} />
                        <Route element={<RequireRole roles={['commercial_director', 'super_admin']} />}>
                            <Route path="org-chart" element={<CommissionOrgChartPage />} />
                        </Route>
                        <Route path="buckler-one" element={<BucklerOnePage />} />                        <Route path="settings" element={<CommissionSettingsPage />} />
                        <Route path="structures" element={<CommissionStructuresPage />} />
                        <Route path="modifiers" element={<CommissionModifiersPage />} />
                        <Route path="goals" element={<CommissionGoalsPage />} />
                        <Route path="goals/:userId/analysis" element={<CommissionGoalBucketAnalysisPage />} />
                        <Route path="teams" element={<SalesTeamsPage />} />
                        <Route path="teams/sellers/:userId/periods" element={<SellerAssignmentPeriodsPage />} />
                        <Route path="submit-nf/:recordId" element={<SubmitCommissionNfPage />} />
                        <Route
                            path="enviar-nf/:recordId"
                            element={<LegacyParamRedirect to={(p) => `/commissioning/submit-nf/${p.recordId}`} />}
                        />
                        <Route path="records" element={<CommissionRecordsPage />} />
                        <Route path="monthly-nfs" element={<CommissionMonthlyNfsPage />} />
                        <Route path="monthly-nfs/:nfId" element={<CommissionMonthlyNfDetailPage />} />
                        <Route path="nf-email-preview" element={<CommissionNfEmailPreviewPage />} />
                        <Route path="records/user/:userId" element={<SellerHistoryPage />} />
                        <Route path="sellers/:userId" element={<SellerHistoryPage />} />
                        <Route path="sellers/:userId/history" element={<SellerCommissionTimelinePage />} />
                        <Route path="sellers/:userId/history/:month" element={<SellerCommissionTimelinePage />} />
                        <Route path="deals/:dealId" element={<CommissionDealDetailPage />} />
                        <Route path="payment-approvals" element={<CommissionPaymentApprovalPage />} />
                        <Route path="payment-approvals/:userId" element={<CommissionPaymentApprovalDetailPage />} />
                        <Route path="payment-rules" element={<CommissionPaymentRulesPage />} />
                        <Route path="cashflow" element={<CommissionCashflowPage />} />
                        <Route path="audit" element={<CommissionAuditPage />} />
                        <Route path="exception-audits" element={<CommissionExceptionAuditPage />} />
                        <Route path="approvers" element={<CommissionApproversPage />} />
                    </Route>
                </Route>
                </Route>

                {/* Inspetor Ploomes — qualquer perfil com InspectFields:PloomesDeal */}
                <Route element={<RequireCanInspectPloomesFields />}>
                    <Route path="commissioning/ploomes-field-inspector" element={<PloomesFieldInspectorPage />} />
                </Route>

                <Route path="*" element={<NotFoundPage />} />
            </Route>
        </Routes>
    );
}
